import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { Accept } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';

import type { GeneralFormProps } from '@/types/general';

import { Button } from '../ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';

type Props = {
  accept?: Accept;
  multiple?: boolean;
  maxSize?: number;
  onFileChange?: (files: File[]) => void;
  hasPreview?: boolean;
  size?: 'small' | 'large';
} & GeneralFormProps;

function RHFShadcnUploadInput({
  name,
  title,
  description,
  required,
  accept,
  multiple = false,
  maxSize,
  onFileChange,
  disabled,
  hasPreview = true,
  size = 'small',
}: Props) {
  const { control, getValues, setValue } = useFormContext();
  const translate = useTranslations();

  const handleDrop = (acceptedFiles: File[]) => {
    const currentFiles = (getValues(name) || []) as File[];
    const uniqueNewFiles = acceptedFiles.filter(
      newFile =>
        !currentFiles.some(
          (existingFile: File) => existingFile.name === newFile.name,
        ),
    );

    const newFiles = multiple
      ? [...currentFiles, ...uniqueNewFiles]
      : uniqueNewFiles;

    if (onFileChange) {
      onFileChange(newFiles);
    } else {
      setValue(name, newFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections }
    = useDropzone({
      onDrop: handleDrop,
      accept: accept || undefined,
      multiple,
      maxSize,
      disabled,
    });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const files = (field.value || []) as File[];
        let dropzoneContent;
        if (size === 'large') {
          dropzoneContent = (
            <div className="flex flex-col gap-5 text-center">
              <div className="text-base font-bold leading-none text-neutral-400">
                {translate('uploads.uploader.dropZoneHint')}
              </div>
              <div className="text-sm font-normal leading-none text-neutral-400">
                {translate('or')}
              </div>
              <Button type="button" variant="outline">
                {translate('uploads.uploader.browserFilesHint')}
              </Button>
            </div>
          );
        } else if (isDragActive) {
          dropzoneContent = (
            <p className="text-blue-400">
              {translate('uploads.uploader.dropZoneHintHovered')}
            </p>
          );
        } else if (files.length > 0) {
          dropzoneContent = (
            <p className="text-sm text-gray-500">
              {`${files.length} file(s) selected`}
            </p>
          );
        } else {
          dropzoneContent = (
            <p className="text-sm text-gray-500">
              {translate('uploads.uploader.dropZoneHintDefault')}
            </p>
          );
        }

        return (
          <FormItem className="size-full">
            <FormLabel>{title}</FormLabel>
            <FormControl>
              <div>
                <div
                  {...getRootProps({
                    className: `dropzone ${
                      disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer'
                    } flex items-center justify-center w-full h-full rounded-md border border-gray-300 shadow-sm transition-shadow duration-300 ease-in-out ${
                      size === 'large' ? 'p-6 bg-neutral-50' : 'p-4 bg-white'
                    } ${
                      isDragActive
                        ? 'shadow-md border-gray-400'
                        : 'hover:shadow-md hover:border-gray-400'
                    } focus:outline-none`, // Remove focus outline
                    style: { pointerEvents: disabled ? 'none' : 'auto' },
                  })}
                >
                  <input
                    {...getInputProps()}
                    disabled={disabled}
                    required={required}
                    className="hidden"
                  />
                  {dropzoneContent}
                </div>
                {fileRejections.length > 0 && (
                  <FormMessage>
                    {translate('uploads.uploader.rejectedFiles')}
                  </FormMessage>
                )}
                {hasPreview && files.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {files.map((file: File, index: number) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={index} className="relative">
                        <Image
                          src={URL.createObjectURL(file)}
                          alt={`preview ${index}`}
                          fill
                          className="h-24 w-full rounded-md object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormControl>
            <FormDescription>{description}</FormDescription>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnUploadInput;
